import React, { useState } from 'react';
import styled from 'styled-components';
import classnames from 'classnames';
import type { HTMLAttributes } from 'react';
import type { MenuItemProps, MenuProps } from './types';
import MaterialIcon from '../icon/Icon.material';
import Collapse from '../transitions/Collapse';
import Tooltip from '../tooltip/Tooltip';
import { DEFAULT_TRANSITION_DURATION, DEFAULT_TRANSITION_FUNC } from '../../util/styled';
import { GLOBAL_ICONS } from '../../util/vars';
import { useLocation } from 'react-router-dom';
import CountryFlag from '../icon/Icon.countryFlag';
import { useI18n } from '@whizz/react-i18n';

const open_classname = 'is-open';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;
type Props = MenuProps & HTMLProps;

const hamburger_size = '20px';

const Menu = React.forwardRef<Ref, Props>(({
  items,
  open,
  handleClick,
}: Props, ref): JSX.Element => {

  const { translate } = useI18n();
  const [ openChildren, setOpenChildren ] = useState<MenuItemProps['id'][]>([]);
  const { pathname } = useLocation();

  const getMenuItem = (item: MenuItemProps) => {

    const extraIcon = () => {
      if (!item?.other) {
        return null;
      }
      const icon = ('target' in item.other) ? 'OpenInNew' : ('waiting' in item.other) ? 'HourglassEmpty' : null;
      if (icon) {
        return (
          <div className={'ml-sm'}>
            <MaterialIcon
              icon={icon}
              color={'muted'}
              size={'sm'}
            />
          </div>);
      }
      return null;
    };
    return !item.hidden && (
      <div className={'flex items-center justify-between py-sm w-full'}>
        <div className={'flex items-center'}>
          {item.icon && (
            <div className={'mr-sm'}>
              { typeof item.icon === 'object' ? <CountryFlag iso2={item.icon.iso2} label={item.label as string} /> : <MaterialIcon icon={item.icon} color={'dark'} size={'sm'} /> }
            </div>
          )}
          {item.label}
        </div>
        {item.children && (
          <div className={'ml-sm'}>
            <MaterialIcon
              icon={GLOBAL_ICONS[openChildren.includes(item.id) ? 'toggle_on' : 'toggle_off']}
              color={'muted'}
              size={'sm'}
            />
          </div>
        )}
        {extraIcon()}
      </div>
    );
  };

  const partialMatch = (pathname: string, match: string | undefined, exactMatch = false): boolean => {
    if (exactMatch) {
      return match === pathname;
    }
    return match ? pathname.indexOf(match) !== -1 : false;
  };

  const getActive = (items: MenuItemProps[]): MenuItemProps | undefined => {
    let found: MenuItemProps | undefined;

    items.forEach((item) => {
      if (item.children) {
        item.children.forEach((child) => {
          if (partialMatch(pathname, child?.other?.href)) {
            found = item;
          }
        });
      }
      if (pathname === item?.other?.href) {
        found = item;
      }
    });

    return found;
  };

  const activeParent = items && getActive(items);

  return (
    <div ref={ref} className={'flex'}>
      <Tooltip
        animation={'perspective'}
        visible={open}
        onClickOutside={handleClick}
        placement={'bottom-end'}
        maxWidth={280}
        tooltipMessage={(
          <div className={'negative-tooltip-padding'}>
            {items ? (
              items
                .filter((item) => !item.hidden)
                .map((item) => (
                  <>
                    <MenuItemStyled
                      key={`menu_item--${item.id}`}
                      data-cy={`menu_item--${item.id}`}
                      as={item.children ? 'button' : item.type ?? 'button'}
                      $isChild={false}
                      $active={partialMatch(pathname, item?.other?.href, item?.exactMatch)}
                      $activeParent={!!(activeParent === item && item.children)}
                      {...item.other}
                      {...item.children && {
                        onClick: () => {
                          let newOpenChildren = [ ...openChildren ];
                          if (newOpenChildren.includes(item.id)) {
                            newOpenChildren = newOpenChildren.filter((child) => item.id !== child);
                          } else {
                            newOpenChildren.push(item.id);
                          }
                          setOpenChildren(newOpenChildren);
                        },
                      }}
                    >
                      {getMenuItem(item)}
                    </MenuItemStyled>
                    {item.children && (
                      <Collapse when={openChildren.includes(item.id)}>
                        {item.children.map((child) => (
                          <>
                            <MenuItemStyled
                              key={`menu_item--${item.id}_child--${child.id}`}
                              as={child.type ?? 'button'}
                              $active={partialMatch(pathname, child?.other?.href)}
                              $isChild
                              {...child.other}
                              {...item.children && {
                                onClick: () => {
                                  let newOpenChildren = [ ...openChildren ];
                                  if (newOpenChildren.includes(child.id)) {
                                    newOpenChildren = newOpenChildren.filter((item) => child.id !== item);
                                  } else {
                                    newOpenChildren.push(child.id);
                                  }
                                  setOpenChildren(newOpenChildren);
                                },
                              }}
                            >
                              {getMenuItem(child)}
                            </MenuItemStyled>
                            {child.children && (
                              <Collapse when={openChildren.includes(child.id)}>
                                {child.children.map((child2) => (
                                  <MenuItemStyled
                                    key={`menu_item--${item.id}_child--${child.id}_child--${child2.id}`}
                                    as={child2.type ?? 'button'}
                                    $active={partialMatch(pathname, child2?.other?.href)}
                                    $isChild
                                    {...child2.other}
                                  >
                                    {getMenuItem(child2)}
                                  </MenuItemStyled>
                                ))}
                              </Collapse>
                            )}
                          </>
                        ))}
                      </Collapse>
                    )}
                  </>
                ))
            ) : <span />}
          </div>
        )}
      >
        <button onClick={handleClick} data-cy="menu-icon" className={'flex justify-center items-center text-primary p-sm space-x-2 sm:border-2 sm:border-primary'}>
          <div className={'font-bold hidden sm:block'}>{translate('components.BUTTONS.MENU')}</div>
          <HamburgerInnerContainer>
            <HamburgerInnerStyled className={classnames({ [open_classname]: open })}/>
          </HamburgerInnerContainer>
        </button>
      </Tooltip>
    </div>
  );
});

const HamburgerInnerContainer = styled('div')`
  position: relative;
  width: ${hamburger_size};
  height: ${`calc(${hamburger_size} / 2)`};
`;

const HamburgerInnerStyled = styled('div')`
  position: absolute;  
  top: 0;
  bottom: 0;
  margin: auto;
  
  &, &:before, &:after {
    position: absolute;
    right: 0;
    width: ${hamburger_size};
    height: ${`calc(${hamburger_size} / 10)`};
    background: ${(props) => props.theme.colors.primary};
  }
  
  &:before {
    content: "";
    top: ${`calc(${hamburger_size} / -3)`};
    transition: top ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC};
  }
  
  &:after {
    content: "";
    bottom: ${`calc(${hamburger_size} / -3)`};
    transition: bottom ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC};
  }
  
  &.${open_classname} {
    &:before {
      top: 0;
    }
    &:after {
      bottom: 0;
    }
  }  
`;

const MenuItemStyled = styled('div')<{ $active: boolean, $activeParent?: boolean, $isChild: boolean; }>`
  display: block;
  width: 100%;
  text-align: left;
  font-size: 1rem;
  padding: ${`calc(${hamburger_size} / 4) calc(${hamburger_size} / 2)`};
  border-bottom: 1px solid transparent;  
  transition: all ${DEFAULT_TRANSITION_FUNC} ${DEFAULT_TRANSITION_DURATION};
  border-color: ${(props) => props.theme.colors.light};
  background: ${(props) => props.theme.colors.white};

  ${(props) => props.$isChild && `
    color: ${props.theme.colors.muted};
    padding-left: ${`calc(${props.theme.sizes.sm} + 0.5rem)`};
    ${!props.$active && `
      &:not(:last-child) {
        border-bottom: none;
      }
    `}
  `}
  
  ${(props) => !props.$isChild && `
    font-weight: bold;
    &:first-child {
      border-top: none;
    }
    &:last-child {
      border-bottom: none;
    }
  `}
  
  &:hover {
    background: ${(props) => props.theme.colors.light};
    color: ${(props) => props.theme.colors.dark};
  }

  ${(props) => props.$active && `
    color: ${props.theme.colors.primary};
    border: 2px solid ${props.theme.colors.primary};
    border-left: 0;
    border-right: 0;
    font-weight: bold;
    svg {
      color: currentColor;
    }
  `}

  ${(props) => props.$activeParent && `
    background: ${props.theme.colors.light};
    color: ${props.theme.colors.dark};
    svg {
      color: currentColor;
    }
  `}
`;

Menu.displayName = 'Menu';

export default Menu;
